import { initializeApp, type FirebaseApp } from 'firebase/app';
import { getMessaging, getToken, isSupported, onMessage, type Messaging } from 'firebase/messaging';

import { getFirebaseVapidKey, getFirebaseWebConfig } from '../config/firebaseEnv';

const FIREBASE_SW_IMPORT_VERSION = '12.12.1';

function fcmLog(...args: unknown[]) {
  console.warn('[FCM]', ...args);
}

let firebaseApp: FirebaseApp | null = null;

let swRegistrationPromise: Promise<ServiceWorkerRegistration | undefined> | null = null;

function getOrInitApp(): FirebaseApp | null {
  const config = getFirebaseWebConfig();
  if (!config) return null;
  if (!firebaseApp) {
    firebaseApp = initializeApp(config);
  }
  return firebaseApp;
}

export async function registerFirebaseMessagingServiceWorker(): Promise<ServiceWorkerRegistration | undefined> {
  if (!swRegistrationPromise) {
    swRegistrationPromise = registerFirebaseMessagingServiceWorkerInner();
  }
  return swRegistrationPromise;
}

async function registerFirebaseMessagingServiceWorkerInner(): Promise<ServiceWorkerRegistration | undefined> {
  const config = getFirebaseWebConfig();
  if (!config || !('serviceWorker' in navigator)) {
    return undefined;
  }

  try {
    const reg = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
    await waitForRegistrationActive(reg);
    return reg;
  } catch (e) {
    fcmLog('Register /firebase-messaging-sw.js failed (add .env + restart, or see scripts/sync-firebase-messaging-sw.js).', e);
  }

  const swSource = `
        importScripts('https://www.gstatic.com/firebasejs/${FIREBASE_SW_IMPORT_VERSION}/firebase-app-compat.js');
        importScripts('https://www.gstatic.com/firebasejs/${FIREBASE_SW_IMPORT_VERSION}/firebase-messaging-compat.js');

        firebase.initializeApp(${JSON.stringify(config)});

        const messaging = firebase.messaging();

        messaging.onBackgroundMessage(function(payload) {
          var data = payload.data || {};
          var notification = payload.notification || {};
          var title = notification.title || data.title || data.notification_title || 'Notification';
          var bodyText = notification.body || data.body || data.notification_body || '';
          var rawId = data.notification_id || data.id || data.notificationId || '';
          var tag = rawId ? 'ncn-' + String(rawId) : 'ncn-' + String(title).slice(0, 48);
          var now = Date.now();
          if (!globalThis.__ncLastBgPush) globalThis.__ncLastBgPush = { tag: '', t: 0 };
          var last = globalThis.__ncLastBgPush;
          if (last.tag === tag && now - last.t < 5000) return;
          last.tag = tag;
          last.t = now;
          self.registration.showNotification(title, { body: bodyText, tag: tag });
        });
      `;

  const blob = new Blob([swSource], { type: 'application/javascript' });
  const swUrl = URL.createObjectURL(blob);

  try {
    const reg = await navigator.serviceWorker.register(swUrl);
    await waitForRegistrationActive(reg);
    return reg;
  } catch (e) {
    URL.revokeObjectURL(swUrl);
    fcmLog('Blob service worker registration failed.', e);
    return undefined;
  }
}

async function waitForRegistrationActive(registration: ServiceWorkerRegistration): Promise<void> {
  if (registration.active?.state === 'activated') return;

  const sw = registration.installing ?? registration.waiting ?? registration.active;
  if (sw && sw.state !== 'activated') {
    await new Promise<void>(resolve => {
      const timeout = window.setTimeout(() => resolve(), 10000);
      const onState = () => {
        if (sw.state === 'activated') {
          window.clearTimeout(timeout);
          sw.removeEventListener('statechange', onState);
          resolve();
        }
      };
      sw.addEventListener('statechange', onState);
      if (sw.state === 'activated') onState();
    });
  }

  try {
    await navigator.serviceWorker.ready;
  } catch {
    /* ignore */
  }
}

export async function obtainFcmToken(): Promise<string | null> {
  const supported = await isSupported().catch(() => false);
  if (!supported) {
    fcmLog('Firebase Messaging is not supported in this browser (use Chrome/Edge/Firefox on desktop, or check iOS Safari limits).');
    return null;
  }

  if (typeof window !== 'undefined' && !window.isSecureContext) {
    fcmLog('Push requires a secure context (HTTPS). localhost is OK; plain HTTP is not.');
    return null;
  }

  const app = getOrInitApp();
  const vapidKey = getFirebaseVapidKey();
  if (!app) {
    fcmLog(
      'Firebase web app not initialized. Set REACT_APP_FIREBASE_API_KEY, AUTH_DOMAIN, PROJECT_ID, MESSAGING_SENDER_ID, APP_ID.',
    );
    return null;
  }
  if (!vapidKey) {
    fcmLog('Missing REACT_APP_FIREBASE_VAPID_KEY (required for getToken).');
    return null;
  }

  if (Notification.permission === 'default') {
    await Notification.requestPermission();
  }
  if (Notification.permission !== 'granted') {
    fcmLog(
      `Notification permission is "${Notification.permission}". Grant permission in the browser to receive push.`,
    );
    return null;
  }
  let messaging: Messaging;
  try {
    messaging = getMessaging(app);
  } catch (e) {
    fcmLog('getMessaging failed', e);
    return null;
  }

  const registration = await registerFirebaseMessagingServiceWorker();
  if (registration) {
    await waitForRegistrationActive(registration);
  }

  const token = await getToken(messaging, {
    vapidKey,
    ...(registration ? { serviceWorkerRegistration: registration } : {}),
  }).catch((err: unknown) => {
    fcmLog('getToken failed (check VAPID key, Firebase project, and service worker).', err);
    return null;
  });

  if (!token) {
    fcmLog('getToken returned empty — device will not register at POST /notifications/devices/.');
  }

  return token ?? null;
}

export type ForegroundPushPayload = { title?: string; body?: string; notificationId?: number };

type ForegroundPushHandler = (payload: ForegroundPushPayload) => void;

let foregroundOnMessageUnsubscribe: (() => void) | null = null;
let activeForegroundHandler: ForegroundPushHandler | null = null;
let activeForegroundSubscriptionId = 0;
let nextForegroundSubscriptionId = 1;

const recentFcmDedupeKeys = new Map<string, number>();
const FCM_DEDUPE_TTL_MS = 30_000;
const FCM_DEDUPE_MAX_KEYS = 64;

function pruneFcmDedupeMap(now: number) {
  recentFcmDedupeKeys.forEach((t, k) => {
    if (now - t > FCM_DEDUPE_TTL_MS) recentFcmDedupeKeys.delete(k);
  });
  while (recentFcmDedupeKeys.size > FCM_DEDUPE_MAX_KEYS) {
    const first = recentFcmDedupeKeys.keys().next().value;
    if (first === undefined) break;
    recentFcmDedupeKeys.delete(first);
  }
}

function shouldSkipDuplicateFcmPayload(payload: {
  messageId?: string;
  data?: Record<string, string | undefined> | undefined;
}): boolean {
  const data = payload.data;
  const dedupeKey =
    payload.messageId ??
    (typeof data?.['gcm.message_id'] === 'string' ? data['gcm.message_id'] : undefined) ??
    (typeof data?.message_id === 'string' ? data.message_id : undefined);
  if (!dedupeKey) return false;

  const now = Date.now();
  pruneFcmDedupeMap(now);
  const key = `fcm:${dedupeKey}`;
  if (recentFcmDedupeKeys.has(key)) return true;
  recentFcmDedupeKeys.set(key, now);
  return false;
}

function parseNotificationIdFromFcmData(data: Record<string, string> | undefined): number | undefined {
  if (!data) return undefined;
  const raw = data.notification_id ?? data.notificationId ?? data.id;
  if (raw == null || raw === '') return undefined;
  const n = Number(typeof raw === 'string' ? raw.trim() : raw);
  return Number.isFinite(n) ? n : undefined;
}

function ensureSingleForegroundOnMessageListener(app: FirebaseApp) {
  if (foregroundOnMessageUnsubscribe) return;

  let messaging: Messaging;
  try {
    messaging = getMessaging(app);
  } catch {
    return;
  }

  foregroundOnMessageUnsubscribe = onMessage(messaging, payload => {
    if (shouldSkipDuplicateFcmPayload(payload)) return;

    const data = payload.data as Record<string, string> | undefined;
    const title = payload.notification?.title ?? data?.title ?? data?.notification_title;
    const body = payload.notification?.body ?? data?.body ?? data?.notification_body ?? '';
    const notificationId = parseNotificationIdFromFcmData(data);

    activeForegroundHandler?.({ title, body, notificationId });
  });
}

export function subscribeForegroundPush(onPayload: ForegroundPushHandler): () => void {
  const app = getOrInitApp();
  if (!app) return () => {};

  const myId = nextForegroundSubscriptionId++;
  ensureSingleForegroundOnMessageListener(app);

  activeForegroundSubscriptionId = myId;
  activeForegroundHandler = onPayload;

  return () => {
    if (activeForegroundSubscriptionId === myId) {
      activeForegroundHandler = null;
      activeForegroundSubscriptionId = 0;
    }
  };
}
